import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { Icon } from '@y42/core/icon';
import { ErrorHandlerService } from '@y42/error-handling';
import { GitQuery } from '@y42/git';
import { OrgQuery } from '@y42/org/state';
import { OrgSpaceQuery } from '@y42/org-space/state';
import { GitFolderToUI } from './git-folder-to-ui.model';
import { Y42GitFolder, Y42GitFolders } from './wasm-git-folders.model';

export interface GitFileDescription {
  gitName: Y42GitFolder | string | undefined;
  icon?: Icon;
  route?: string;
  component?: GitUiDisplayComponentType;
  displayText?: string;
  useInRouteAs?: string | ((value: string) => [string, string]);
  canRevert?: boolean;
  children?: GitFileDescription[];
}

export abstract class GitUiDisplayComponent {
  abstract path: string;
  // other props or methods
}

export type SubClassOfGitUiDisplayComponent = (new () => GitUiDisplayComponent) & {
  [K in keyof typeof GitUiDisplayComponent]: (typeof GitUiDisplayComponent)[K];
};

export type GitUiDisplayComponentType =
  | (() => Promise<SubClassOfGitUiDisplayComponent>)
  | SubClassOfGitUiDisplayComponent;

export interface GitPathMapConfig {
  component?: GitUiDisplayComponentType;
  route?: string;
  displayText?: string;
}
export type GitPathMap = [string, GitPathMapConfig];

export interface GitPathIconMapConfig {
  icon: Icon;
}
export type GitPathIconMap = [string, GitPathIconMapConfig];

export interface GitPathInfo {
  folder?: Y42GitFolders;
  displayText?: string;
  icon?: Icon;
  featureLogo?: Icon;
  route?: string;
  routeQueryParams?: Params | null;
  canRevert?: boolean;
}

/**
 * @deprecated please use the `FeatureAggregationService` instead
 */
@Injectable({ providedIn: 'root' })
export class GitPathMapper {
  constructor(
    private readonly orgQuery: OrgQuery,
    private readonly gitQuery: GitQuery,
    private readonly orgSpaceQuery: OrgSpaceQuery,
    private readonly errorHandler: ErrorHandlerService,
  ) {}

  private pathMap: Readonly<GitFileDescription[]> = [];

  register(gitFileDescription: Readonly<GitFileDescription[]>) {
    this.pathMap = gitFileDescription;
  }

  getComponent(path: string): Promise<SubClassOfGitUiDisplayComponent> | null {
    const pathMap = this.getPathMap(path);
    if (!pathMap) {
      return null;
    }

    const { config } = pathMap;
    if (!config.component) {
      return null;
    }

    const componentType = config.component;

    if (typeof componentType === 'function') {
      // TODO find more type-safe way than type assertion
      // eslint-disable-next-line @typescript-eslint/ban-types
      return (componentType as Function)();
    }

    return Promise.resolve(componentType as SubClassOfGitUiDisplayComponent);
  }

  getPathInfo(
    path: string,
    customReplacements: {
      org_slug: string;
      space_id: string;
      branch_name: string;
    } | null = null,
  ): GitPathInfo | null {
    const pathMap = this.getPathMap(path);
    if (!pathMap) {
      return null;
    }
    const { config, routeReplacements } = pathMap;

    const pathInfo = {} as GitPathInfo;

    const replacements: Record<string, string> = {
      ...routeReplacements,
      org_slug: this.orgQuery.getActive()?.slug || 'not-found-slug',
      space_id: this.orgSpaceQuery.getActiveId() || 'not-found-space',
      branch_name: this.gitQuery.getActiveBranchName() || 'not-found-branch',
      ...customReplacements,
    };

    const subFeature = replacements?.sub_feature;
    const feature = (subFeature ? `${replacements.feature}/${subFeature}` : replacements.feature) as Y42GitFolders;

    if (replacements?.sub_feature) {
      replacements.sub_feature = replacements.sub_feature.toLowerCase();
    }

    pathInfo.canRevert = !!config.canRevert;

    if (config.route) {
      replacements.feature = replacements.feature ? replacements.feature.toLowerCase() : '';
      pathInfo.route = this.replaceTemplate(config.route, replacements);

      const routeParams = extractRouteParams(pathInfo.route);
      if (routeParams) {
        pathInfo.route = routeParams.route;
        pathInfo.routeQueryParams = routeParams.params;
      }
    }

    if (config.displayText) {
      if (replacements.feature?.endsWith('s')) {
        // singularize feature name
        replacements.feature = replacements.feature.slice(0, -1);
      }
      pathInfo.displayText = this.replaceTemplate(config.displayText, replacements);
    }

    if (config.icon) {
      pathInfo.icon = config.icon;
    }

    if (GitFolderToUI[feature]) {
      pathInfo.featureLogo = GitFolderToUI[feature].logo;
    }

    pathInfo.folder = feature;

    return pathInfo;
  }

  private getPathMap(gitFilePath: string) {
    const parts = gitFilePath.split('/').filter(Boolean);

    const routeReplacements: Record<string, string> = {};
    if (this.pathMap.length === 0) {
      this.errorHandler.error(
        'GitPathMapper.getPathMap pathMap is empty. Probably you did not register any configuration.',
      );
    }
    let currentMapItem = this.pathMap;
    let config!: GitFileDescription;

    for (const part of parts) {
      const next = currentMapItem.find((a) => a.gitName === part) ?? currentMapItem.find((a) => !a.gitName);
      if (!next) {
        // console.error(`File structure is broken, '${gitFilePath}' not found`);
        return null;
      }

      if (next.useInRouteAs) {
        const [key, value] =
          typeof next.useInRouteAs === 'string' ? [next.useInRouteAs, part] : next.useInRouteAs(part);

        routeReplacements[key] = value;
      }
      config = next;
      currentMapItem = next.children ?? [];
    }
    return { config, routeReplacements };
  }

  private replaceTemplate(template: string, replacements: Record<string, string>) {
    try {
      return template.replace(/\[([^[\]]+)]/g, (_, key) => {
        const replacement = replacements[key];
        if (!replacement) {
          console.error(`There is no dedicated replacement for [${key}] in ${template}`);
          return '';
        }

        return replacement;
      });
    } catch (err) {
      console.error(err);
      // wrong replacement
      return undefined;
    }
  }
}

export function extractRouteParams(path?: string): { route: string; params: Params } | null {
  if (!path || !path.includes('?')) {
    return null;
  }

  const [route, queryParams] = path.split('?');

  if (!route) {
    return null;
  }

  try {
    const searchParams = new URLSearchParams(queryParams);
    const params = {} as Params;
    searchParams.forEach((value, key) => {
      params[key] = value;
    });

    return {
      route,
      params,
    };
  } catch (e) {
    console.error(`Invalid query params ${queryParams}`);
    return null;
  }
}
